import {
  AppleFilled,
  GoogleCircleFilled,
  MobileFilled,
} from '@ant-design/icons'
import { AvailableLocale } from '@models/Lang'
import { Alert, Flex, Segmented, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { resolveWalletCard } from './defaults'
import { WALLET_FIELD_PLATFORMS } from './highlight'
import AppMemberCardFace from './faces/AppMemberCardFace'
import ApplePassFace from './faces/ApplePassFace'
import GooglePassFace from './faces/GooglePassFace'
import { WalletCardPlatform, WalletCardPreviewProps } from './types'
import { useWalletAssetUrl } from './useWalletAssetUrl'
import WalletFieldZone from './WalletFieldZone'

const { Text, Paragraph } = Typography

/**
 * Labels for the back-of-card rows, matching ApplePassBuilder::localeStrings
 * (TERMS / EMAIL / PHONE / WEBSITE) so the preview reads like the real pass
 * back — each value under its own heading rather than run together.
 */
const BACK_LABELS: Record<
  AvailableLocale,
  { terms: string; email: string; phone: string; website: string }
> = {
  fr: {
    terms: 'Conditions',
    email: 'E-mail',
    phone: 'Téléphone',
    website: 'Site web',
  },
  en: { terms: 'Terms', email: 'Email', phone: 'Phone', website: 'Website' },
}

/** One back-of-card row: its heading above the value, like the real pass back. */
const BackField = ({ label, value }: { label: string; value: string }) => (
  <Flex vertical gap={2}>
    <Text
      strong
      style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}
    >
      {label}
    </Text>
    <Text style={{ fontSize: 13 }}>{value}</Text>
  </Flex>
)

const PLATFORM_OPTIONS: {
  label: string
  value: WalletCardPlatform
  icon: React.ReactNode
}[] = [
  { label: 'Apple', value: 'apple', icon: <AppleFilled /> },
  { label: 'Google', value: 'google', icon: <GoogleCircleFilled /> },
  { label: 'App', value: 'app', icon: <MobileFilled /> },
]

/**
 * Faithful, live preview of the wallet card configuration for a program:
 * Apple storeCard, Google generic card, and the in-app member card, behind a
 * single switcher so the admin compares them without splitting attention.
 * Pure presentation — every value comes from props, nothing is fetched.
 *
 * `activeField` / `onActiveFieldChange` link each region to its form control:
 * a region lights up while its field is active, and hovering a region marks
 * the field active so the matching input highlights in turn.
 */
const WalletCardPreview = ({
  brandLine,
  showBrandLineIos,
  cardTitle,
  backgroundColor,
  foregroundColor,
  labelColor,
  logo,
  icon,
  strip,
  hero,
  showExpiry,
  terms,
  supportEmail,
  supportPhone,
  supportUrl,
  member,
  programName,
  locale,
  className,
  style,
  activeField,
  onActiveFieldChange,
}: WalletCardPreviewProps) => {
  const [platform, setPlatform] = useState<WalletCardPlatform>('apple')
  const highlight = { activeField, onActiveFieldChange }
  const iconUrl = useWalletAssetUrl(icon)
  const backLabels = BACK_LABELS[locale ?? 'en']

  // When a field is focused/hovered that has no region on the face currently
  // shown, flip to a face that does — otherwise its highlight lands off-screen
  // and the admin "doesn't see where it goes" (e.g. the hero image lives only
  // on Google, the strip only on Apple).
  useEffect(() => {
    if (!activeField) return
    const platforms = WALLET_FIELD_PLATFORMS[activeField]
    if (!platforms.length) return
    setPlatform((prev) => (platforms.includes(prev) ? prev : platforms[0]))
  }, [activeField])

  const { resolved: card, defaultsUsed } = resolveWalletCard({
    brandLine,
    cardTitle,
    backgroundColor,
    foregroundColor,
    labelColor,
    showExpiry,
    terms,
    supportEmail,
    supportPhone,
    supportUrl,
    member,
    programName,
  })

  const defaultedGroups = [
    defaultsUsed.brandLine && 'brand line',
    defaultsUsed.cardTitle && 'card title',
    defaultsUsed.colors && 'colors',
  ].filter(Boolean) as string[]

  // Back-of-card content (Apple backFields / Google textModules + links) has
  // no equivalent on the in-app card, so showing it under the App face would
  // claim a screen that doesn't exist there.
  const showBackOfCard = platform !== 'app'
  const hasSupportInfo = Boolean(
    terms || supportEmail || supportPhone || supportUrl
  )

  return (
    <Flex
      vertical
      align="center"
      gap={16}
      className={className}
      style={{ ...style, width: '100%' }}
    >
      {/* Switcher stays put at the top of the panel; everything below scrolls,
          so the card is reachable even when it's taller than the viewport. The
          height cap + column live on the sticky wrapper (WalletCardTab) so this
          scroll box has a bounded height to pin against. */}
      <Segmented
        style={{ flexShrink: 0 }}
        value={platform}
        onChange={(value) => setPlatform(value as WalletCardPlatform)}
        options={PLATFORM_OPTIONS}
      />

      <Flex
        vertical
        align="center"
        gap={16}
        style={{ width: '100%', flex: 1, minHeight: 0, overflowY: 'auto' }}
      >
        {defaultedGroups.length > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ width: '100%' }}
            message={`Not configured yet — showing the default ${defaultedGroups.join(', ')}`}
          />
        )}

        {platform === 'apple' && (
          <ApplePassFace
            card={card}
            logo={logo}
            strip={strip}
            // logoText only when the admin set a brand line AND opted to show it
            // on the Apple pass — exactly as ApplePassBuilder gates it. The
            // default is never previewed as a line beside the logo.
            logoText={
              !defaultsUsed.brandLine && showBrandLineIos
                ? card.brandLine
                : undefined
            }
            locale={locale}
            {...highlight}
          />
        )}
        {platform === 'google' && (
          <GooglePassFace
            card={card}
            logo={logo}
            hero={hero}
            locale={locale}
            {...highlight}
          />
        )}
        {platform === 'app' && (
          <AppMemberCardFace
            card={card}
            programName={card.programName}
            logo={logo}
            locale={locale}
            {...highlight}
          />
        )}

        {/* The icon has no place on the pass face — it's the icon Apple shows in
            notifications and the pass list — so it's previewed on its own below
            the card. */}
        {platform === 'apple' && (
          <WalletFieldZone
            field="icon"
            {...highlight}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              padding: 8,
            }}
          >
            {iconUrl ? (
              <img
                src={iconUrl}
                alt="Icon"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: 'rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(0, 0, 0, 0.45)',
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {card.brandLine.charAt(0)}
              </div>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              Icon — notifications &amp; pass list
            </Text>
          </WalletFieldZone>
        )}

        {showBackOfCard && (
          <Flex vertical style={{ width: '100%' }} gap={8}>
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Back of pass / support info
            </Text>
            {hasSupportInfo ? (
              <Flex vertical gap={10}>
                {terms && (
                  <WalletFieldZone
                    field="terms"
                    {...highlight}
                    style={{ padding: '2px 0' }}
                  >
                    <Flex vertical gap={2}>
                      <Text
                        strong
                        style={{
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        {backLabels.terms}
                      </Text>
                      <Paragraph
                        type="secondary"
                        style={{ fontSize: 12, marginBottom: 0 }}
                        ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                      >
                        {terms}
                      </Paragraph>
                    </Flex>
                  </WalletFieldZone>
                )}
                {(supportEmail || supportPhone || supportUrl) && (
                  <WalletFieldZone
                    field="support"
                    {...highlight}
                    style={{ padding: '2px 0' }}
                  >
                    <Flex vertical gap={8}>
                      {supportEmail && (
                        <BackField
                          label={backLabels.email}
                          value={supportEmail}
                        />
                      )}
                      {supportPhone && (
                        <BackField
                          label={backLabels.phone}
                          value={supportPhone}
                        />
                      )}
                      {supportUrl && (
                        <BackField
                          label={backLabels.website}
                          value={supportUrl}
                        />
                      )}
                    </Flex>
                  </WalletFieldZone>
                )}
              </Flex>
            ) : (
              <Text type="secondary" italic style={{ fontSize: 12 }}>
                Not set
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

export default WalletCardPreview
